import { useState } from 'react'

import { type StartResult } from '@/platform/access/learner-session'
import { PIN_MAX_LENGTH, PIN_MIN_LENGTH } from '@/platform/family/api'
import { toAppError } from '@/shared/errors/envelope'
import { PinPad } from '@/shared/ui/PinPad'

import { formatSeconds, useCountdown } from './countdown'
import { LearnerAvatar } from './entry-ui'

/**
 * Screens D and F (D-16): the learner enters their own PIN. The PIN is checked
 * by the server only (`start_learner_session`); answers are generic and the
 * lock (5 wrong → 5 minutes per learner, plus a family-wide limit) is shown as
 * a countdown. Used for first entry, for returning learners whose session
 * ended, and over Child Home when the session expires (`expired`).
 */

export type SessionStarted = Extract<StartResult, { status: 'ok' }>

export interface LearnerPinScreenProps {
  readonly learner: { readonly id: string; readonly nickname: string }
  readonly onStart: (pin: string) => Promise<StartResult>
  readonly onStarted: (session: SessionStarted) => void
  readonly onBack: () => void
  /** The session ended while learning: explain why the PIN is asked again. */
  readonly expired?: boolean
  readonly backLabel?: string
  readonly now?: () => number
}

export function LearnerPinScreen({
  learner,
  onStart,
  onStarted,
  onBack,
  expired = false,
  backLabel = 'Quay lại',
  now = Date.now,
}: LearnerPinScreenProps): React.JSX.Element {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lockedUntil, setLockedUntil] = useState<string | null>(null)
  const [noPin, setNoPin] = useState(false)
  const lockSeconds = useCountdown(lockedUntil, now)

  const submit = async (pin: string) => {
    setBusy(true)
    setError(null)
    try {
      const result = await onStart(pin)
      switch (result.status) {
        case 'ok':
          onStarted(result)
          return
        case 'wrong_pin':
          setError(
            `Mã PIN chưa đúng. Con thử lại nhé (còn ${String(result.attempts_remaining)} lần).`,
          )
          return
        case 'locked':
          setLockedUntil(result.locked_until)
          return
        case 'pin_not_configured':
          setNoPin(true)
          return
      }
    } catch (e) {
      setError(
        toAppError(e).code === 'SESSION_EXPIRED'
          ? 'Phiên đăng nhập của bố mẹ đã hết. Nhờ bố mẹ đăng nhập lại nhé.'
          : 'Chưa kiểm tra được mã PIN. Con thử lại nhé.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col gap-5 px-6 pt-4 pb-10">
      <button
        type="button"
        className="min-h-11 self-start px-1 text-lg text-sky-800 underline dark:text-sky-300"
        onClick={onBack}
      >
        ← {backLabel}
      </button>

      <div className="flex flex-col items-center gap-2 text-center">
        <LearnerAvatar id={learner.id} nickname={learner.nickname} size="xl" />
        <p className="text-2xl font-bold">{learner.nickname}</p>
        {expired && (
          <p role="status" className="text-lg text-slate-700 dark:text-slate-300">
            Con đã nghỉ lâu nên cần nhập lại mã PIN để học tiếp.
          </p>
        )}
      </div>

      {noPin ? (
        <p role="alert" className="text-center text-lg">
          Hồ sơ này chưa có mã PIN. Con nhờ bố mẹ đặt mã PIN nhé.
        </p>
      ) : lockSeconds > 0 ? (
        <div role="alert" className="flex flex-col items-center gap-2 text-center">
          <p className="text-4xl" aria-hidden>
            ⏳
          </p>
          <p className="text-lg font-semibold">Nhập sai nhiều lần quá rồi</p>
          <p className="text-slate-700 dark:text-slate-300">
            Con đợi {formatSeconds(lockSeconds)} rồi thử lại nhé.
          </p>
        </div>
      ) : (
        <PinPad
          title="Nhập mã PIN của con"
          error={error}
          minLength={PIN_MIN_LENGTH}
          maxLength={PIN_MAX_LENGTH}
          busy={busy}
          submitLabel="Vào học"
          onSubmit={(pin) => void submit(pin)}
        />
      )}
    </main>
  )
}
